import {DetailPanel, DetailKvTable} from "../../../components/detailLayout";
import React, {useCallback, useEffect, useRef, useState} from "react";
import Th_title from "../../../components/Th_title";
import ReadOnlyInputBasic from "../../../components/input/ReadOnlyInputBasic";
import {ContentStatus} from "../../../constant/CodeSpecNum";
import {useDispatch, useSelector} from "react-redux";
import TextAreaBasic from "../../../components/input/TextAreaBasic";
import {ButtonBasic} from "../../../components/buttons/ButtonBasic";
import {handleActiveError, handleActiveToast} from "../../../components/sonner/ToastBasic";
import {changeRefreshContentStatus} from "../../../redux/slice/statusSlice";
import {getReworkList} from "../../../api/contentStatus/contentStatusService";
import {changeStatusNumericValue} from "../../../redux/slice/contentSlice";
import {useTranslation} from "react-i18next";
import {convertDate, handleValidation} from "../../serviceRequest/serviceRequest/serviceUtils";
import SimmsDatePicker from "../../../components/datepicker/SimmsDatePicker";
import {dateOnChange} from "../../../components/commonmethod/simmsDateMethod";
import {EXECUTION_RECORD_CONFIG} from "./executionRecordConfig";
import MaintenanceVerification from "./MaintenanceVerification";
import {getMaintenanceVerificationConfig, resolveVerificationEndDateField} from "./maintenanceVerificationConfig";
import {workDone, workSave, workUpdate} from "../../../api/process/ProcessService";
import {buildWorkProcess, formatWorkCloseDateDisplay} from "./workProcessUtils";
import {getVisibleDocs} from "../../../components/files/editAttachState";
import WorkAttachField from "./WorkAttachField";
import {
    TaskActivityCycleCard,
    TaskActivityFeed,
    TaskActivityFeedSort,
    TaskActivityItem,
} from "./TaskProgressThread";
import {
    buildWorkAttachStateFromFiles,
    findReworkEntry,
    normalizeReworkListResponse,
    parseReworkResponseCycle,
    resolveAttachVerificationCycleKey,
    needsFreshWorkInput,
    resolveCurrentDraftWork,
    resolvePendingVerificationWork,
    resolveCurrentWorkReworkId,
    resolveReworkDeadLine,
    reworkFileTable,
    sortReworkListByTime,
} from "./reworkResponseUtils";
import {
    resolveWorkAuthor,
    resolveWorkUser,
} from "./taskProgressThreadUtils";

function isLatestReworkWorkSame(reworkList, workBody) {
    if (!workBody) return false;
    const list = normalizeReworkListResponse(reworkList);
    if (!list.length) return false;
    const {work} = parseReworkResponseCycle(list[list.length - 1]);
    return work?.body === workBody;
}

function ReworkHistoryThread({
    items,
    sortOrder = "latest",
    verificationCompose,
    verificationComposeEntryKey,
    excludeReworkIds = [],
    requestEndDate,
}) {
    const list = sortReworkListByTime(items, sortOrder);
    if (!list.length) return null;
    const excludedIds = new Set(excludeReworkIds.filter((id) => id != null));

    return list.flatMap((entry, index) => {
        if (entry.id != null && excludedIds.has(entry.id)) return [];

        const {work, verification} = parseReworkResponseCycle(entry);
        const primary = work || verification;
        if (!primary) return [];

        const workBody = work?.body;
        const verificationBody = verification?.body
            && verification.body !== work?.body
            ? verification.body
            : (!work ? verification?.body : undefined);
        const fileJoinKey = work?.fileJoinKey ?? entry.id;
        const fileTable = work?.files?.length
            ? undefined
            : reworkFileTable(fileJoinKey);
        const entryKey = entry.id ?? `rework-${index}`;
        const attachVerification = Boolean(
            verificationCompose
            && verificationComposeEntryKey
            && verificationComposeEntryKey === entryKey,
        );
        const suppressVerificationForCompose = attachVerification && !verification?.passed;

        return [(
            <TaskActivityCycleCard
                key={entryKey}
                user={primary.user}
                name={primary.name || undefined}
                time={primary.time}
                workBody={workBody}
                workDeadLine={work?.deadLine || requestEndDate}
                verification={suppressVerificationForCompose ? null : verification}
                verificationBody={suppressVerificationForCompose ? undefined : verificationBody}
                files={work?.files}
                fileTable={fileTable}
                verificationCompose={attachVerification ? verificationCompose : undefined}
            />
        )];
    });
}

function ActiveExecutionEditor({
    data,
    config,
    contentCategory,
    currentWorkReworkId,
    draftWork,
    freshWorkInput = false,
    inProgressData,
    setInProgressData,
    requestEndDate,
    onComplete,
    onReworkListRefresh,
    refreshContent,
    readOnly = false,
    embedded = false,
    contentLabel = "작업내역",
}) {
    const dispatch = useDispatch();
    const currentUser = useSelector((state) => state.auth.user);
    const workUser = resolveWorkUser(data) || currentUser;
    const workAuthor = resolveWorkAuthor(data) || currentUser?.name;
    const hasContentOnServer = !freshWorkInput && Boolean(
        data.inProgressDescription?.trim()
        || draftWork?.content?.trim(),
    );
    const [contentSaved, setContentSaved] = useState(hasContentOnServer);
    const [isEditing, setIsEditing] = useState(!hasContentOnServer);
    const [editSnapshot, setEditSnapshot] = useState("");
    const textareaWrapRef = useRef(null);
    const shouldFocusTextareaRef = useRef(false);
    const canUpdate = data.auth?.update ?? data.rudAuthDto?.update;
    const [attachState, setAttachState] = useState(() => buildWorkAttachStateFromFiles(draftWork?.files));

    const hasDescription = Boolean(inProgressData.inProgressDescription?.trim());
    const isSavedOnServer = contentSaved || hasContentOnServer;
    const canComplete = !readOnly && isSavedOnServer && hasDescription && !isEditing;
    const showTextarea = !readOnly && (!isSavedOnServer || isEditing);
    const showDatePicker = !readOnly && (!isSavedOnServer || isEditing);
    const fileEditable = !readOnly && canUpdate && (isEditing || !isSavedOnServer);
    const visibleAttachDocs = getVisibleDocs(attachState);
    const draftFileIds = draftWork?.files?.map((file) => file?.id).filter((id) => id != null).join(",") ?? "";

    const startEdit = () => {
        const current = inProgressData.inProgressDescription ?? data.inProgressDescription ?? "";
        setEditSnapshot(current);
        shouldFocusTextareaRef.current = true;
        setIsEditing(true);
    };

    useEffect(() => {
        if (!isEditing || !shouldFocusTextareaRef.current) return;
        const textarea = textareaWrapRef.current?.querySelector("textarea");
        if (!textarea) return;
        shouldFocusTextareaRef.current = false;
        requestAnimationFrame(() => {
            textarea.focus();
            const end = textarea.value.length;
            textarea.setSelectionRange(end, end);
        });
    }, [isEditing]);

    const cancelEdit = () => {
        setInProgressData((prev) => ({...prev, inProgressDescription: editSnapshot}));
        setAttachState(buildWorkAttachStateFromFiles(draftWork?.files));
        setIsEditing(false);
    };

    const save = async () => {
        if (!inProgressData.inProgressDescription?.trim()) {
            window.alert("내용을 입력해주세요.");
            return;
        }
        if (!window.confirm("저장하시겠습니까?")) return;

        const wasSaved = isSavedOnServer;
        const workTime = inProgressData.inProgressCloseDate || convertDate();
        const isUpdate = wasSaved && (currentWorkReworkId ?? draftWork?.reworkId);
        const reworkId = currentWorkReworkId ?? draftWork?.reworkId;
        const contents = buildWorkProcess({
            type: config.reworkType,
            content: inProgressData.inProgressDescription,
            workTime,
            operatorId: currentUser?.id,
            deletedFileIds: isUpdate ? attachState.removedFileIds : undefined,
        });
        const pendingFiles = attachState.pendingDocs ?? [];
        const response = isUpdate
            ? await workUpdate(data.id, reworkId, contents, pendingFiles)
            : await workSave(data.id, contents, pendingFiles);
        if (response?.status === 200) {
            await onReworkListRefresh?.();
            dispatch(changeRefreshContentStatus(!refreshContent));
            setInProgressData({
                inProgressDescription: inProgressData.inProgressDescription,
                inProgressCloseDate: workTime,
            });
            setContentSaved(true);
            setIsEditing(false);
            setEditSnapshot("");
            handleActiveToast({
                message: wasSaved ? "정보가 수정되었습니다." : "정보가 등록되었습니다.",
            });
        } else {
            handleActiveError({message: "서버에러"});
        }
    };

    useEffect(() => {
        const description = freshWorkInput
            ? ""
            : (data.inProgressDescription?.trim()
                || draftWork?.content?.trim()
                || "");
        setInProgressData((prev) => ({
            inProgressCloseDate: freshWorkInput
                ? convertDate()
                : (data.inProgressCloseDate || draftWork?.workTime || prev.inProgressCloseDate || convertDate()),
            inProgressDescription: description || prev.inProgressDescription || "",
        }));
        const saved = !freshWorkInput && Boolean(
            data.inProgressDescription?.trim() || draftWork?.content?.trim(),
        );
        setContentSaved(saved);
        setIsEditing(readOnly ? false : !saved);
        setEditSnapshot("");
    }, [
        data.id,
        data.inProgressCloseDate,
        data.inProgressDescription,
        draftWork?.content,
        draftWork?.workTime,
        draftWork?.reworkId,
        freshWorkInput,
        readOnly,
        setInProgressData,
    ]);

    useEffect(() => {
        if (isEditing) return;
        setAttachState(buildWorkAttachStateFromFiles(draftWork?.files));
    }, [data.id, draftWork?.reworkId, draftFileIds, isEditing]);

    const workDatePicker = showDatePicker ? (
        <SimmsDatePicker
            time
            auth
            data={inProgressData.inProgressCloseDate || convertDate()}
            onChange={(v) => dateOnChange(setInProgressData, "inProgressCloseDate", v, false)}
        />
    ) : null;

    const workDateReadOnly = !showDatePicker && inProgressData.inProgressCloseDate ? (
        <span className="task-activity-compose__close-date-text">
            <span className="task-activity-compose__close-date-label">작업일자</span>
            <span className="task-activity-compose__close-date-value">
                {formatWorkCloseDateDisplay(inProgressData.inProgressCloseDate)}
            </span>
        </span>
    ) : null;

    const actionButtonGroup = (
        <div className="detail-kv-actions__buttons">
            {!isSavedOnServer && (
                <>
                    <ButtonBasic
                        text="저장"
                        className="detail-list-summary-action is-muted"
                        size="none"
                        onClick={save}
                    />
                    <ButtonBasic
                        text="진행완료"
                        className="detail-list-summary-action"
                        size="none"
                        disabled={!canComplete}
                        onClick={onComplete}
                    />
                </>
            )}
            {isSavedOnServer && !isEditing && (
                <>
                    <ButtonBasic
                        text="수정"
                        className="detail-list-summary-action is-muted"
                        size="none"
                        onClick={startEdit}
                    />
                    <ButtonBasic
                        text="진행완료"
                        className="detail-list-summary-action"
                        size="none"
                        disabled={!canComplete}
                        onClick={onComplete}
                    />
                </>
            )}
            {isSavedOnServer && isEditing && (
                <>
                    <ButtonBasic
                        text="저장"
                        className="detail-list-summary-action is-muted"
                        size="none"
                        onClick={save}
                    />
                    <ButtonBasic
                        text="취소"
                        className="detail-list-summary-action is-muted"
                        size="none"
                        onClick={cancelEdit}
                    />
                </>
            )}
        </div>
    );

    const actionButtons = !readOnly && (
        <div className={`detail-kv-actions${embedded ? " task-activity-compose__actions" : ""}`}>
            {!embedded && (
                <div className="task-activity-compose__close-date">
                    {workDatePicker || workDateReadOnly}
                </div>
            )}
            {embedded && showDatePicker && (
                <div className="task-activity-compose__close-date">
                    {workDatePicker}
                </div>
            )}
            {!embedded && (
                <span
                    className={`detail-kv-actions__hint${canComplete || isEditing ? " is-empty" : ""}`}
                    aria-hidden={canComplete || isEditing}
                >
                    {!isSavedOnServer
                        ? "내용을 입력하고 저장하면 진행완료할 수 있습니다."
                        : "내용을 입력해주세요."}
                </span>
            )}
            {actionButtonGroup}
        </div>
    );

    if (embedded) {
        return (
            <TaskActivityItem
                user={workUser}
                name={workAuthor || undefined}
                workDate={!showDatePicker ? inProgressData.inProgressCloseDate : undefined}
                workDeadLine={requestEndDate}
            >
                <div className="task-activity-compose">
                    <div className="task-activity-compose__input" ref={textareaWrapRef}>
                        {showTextarea ? (
                            <TextAreaBasic
                                target="inProgressDescription"
                                placeholder="작업 내용을 작성해주세요."
                                data={{data: inProgressData, setData: setInProgressData}}
                            />
                        ) : (
                            <div className="task-activity-text">{inProgressData.inProgressDescription}</div>
                        )}
                    </div>
                    {(fileEditable || visibleAttachDocs.length > 0) && (
                        <div className="task-activity-compose__files">
                            <WorkAttachField
                                variant="kv"
                                readOnly={!fileEditable}
                                attachState={attachState}
                                setAttachState={setAttachState}
                            />
                        </div>
                    )}
                    {actionButtons}
                </div>
            </TaskActivityItem>
        );
    }

    const editorTable = (
        <DetailKvTable>
            <tbody>
            {readOnly && data.inProgressCloseDate && (
                <tr>
                    <Th_title title="작업일자" className="fieldset-td" colspan={3}>
                        <ReadOnlyInputBasic target="inProgressCloseDate" value={data.inProgressCloseDate} />
                    </Th_title>
                </tr>
            )}
            {readOnly && config.showCloseUser && data.inProgressCloseUser && (
                <tr>
                    <Th_title title="처리 담당자" className="fieldset-td" colspan={3}>
                        <div>{data.inProgressCloseUser.name}</div>
                    </Th_title>
                </tr>
            )}
            <tr>
                <Th_title title="내용" className="fieldset-td" colspan={3} required={!readOnly}>
                    <div className="detail-kv-field-stack">
                        <div className="detail-kv-field-stack__input" ref={textareaWrapRef}>
                            {showTextarea ? (
                                <TextAreaBasic
                                    target="inProgressDescription"
                                    placeholder="내용을 작성해주세요."
                                    data={{data: inProgressData, setData: setInProgressData}}
                                />
                            ) : (
                                <div className="align-text-div">{inProgressData.inProgressDescription}</div>
                            )}
                        </div>
                        {actionButtons}
                    </div>
                </Th_title>
            </tr>
            <tr className="asset-detail-kv-row--auto">
                <Th_title title="파일" className="fieldset-td" colspan={3}>
                    <WorkAttachField
                        readOnly={!fileEditable}
                        attachState={attachState}
                        setAttachState={setAttachState}
                    />
                </Th_title>
            </tr>
            </tbody>
        </DetailKvTable>
    );

    return (
        <DetailPanel title="작업내역">
            {editorTable}
        </DetailPanel>
    );
}

function TaskProgressPanel({
    title,
    statusRows,
    reworkItems,
    currentWorkReadOnly,
    activeEditor,
    verificationVariant,
    verificationStepActive = false,
    verificationData,
    verificationFeedWorkContext,
    verificationWorkBody,
    verificationAttachReworkId = null,
    verificationReviewContext,
    historyExcludeReworkIds = [],
    requestEndDate,
}) {
    const [feedSort, setFeedSort] = useState("latest");
    const hasHistory = reworkItems?.length > 0;
    const enableVerificationFeed = Boolean(
        verificationVariant
        && (
            verificationStepActive
            || verificationFeedWorkContext
            || verificationWorkBody
            || verificationReviewContext
        )
    );

    const hasThread = hasHistory || currentWorkReadOnly || activeEditor || enableVerificationFeed;



    const verificationAttachKey = verificationFeedWorkContext
        ? null
        : (verificationAttachReworkId != null
            ? verificationAttachReworkId
            : (verificationReviewContext?.reworkId != null && hasHistory
                ? verificationReviewContext.reworkId
                : (verificationVariant && verificationWorkBody && hasHistory
                    ? resolveAttachVerificationCycleKey(reworkItems, verificationWorkBody, feedSort)
                    : null)));

    const verificationCompose = enableVerificationFeed && verificationVariant ? (
        <MaintenanceVerification
            embedded
            embeddedLayout="feed"
            data={verificationData}
            variant={verificationVariant}
            suppressCompletedInFeed={hasHistory}
            feedAttachToPriorWork={Boolean(verificationAttachKey)}
            feedWorkContext={verificationAttachKey ? null : verificationFeedWorkContext}
            reviewContext={verificationReviewContext}
        />
    ) : null;

    if (!statusRows && !hasThread) {
        return null;
    }

    return (
        <DetailPanel title={title}>
            {/* 요청진행상태 & 처리담당자 등 */}
            {statusRows && (
                <DetailKvTable>
                    <tbody>{statusRows}</tbody>
                </DetailKvTable>
            )}


            {hasThread && (
                <TaskActivityFeed
                    sortControl={hasHistory ? (
                        <TaskActivityFeedSort value={feedSort} onChange={setFeedSort} />
                    ) : null}
                >
                    {activeEditor}
                    {!verificationAttachKey && verificationCompose}
                    {hasHistory && (
                        <ReworkHistoryThread
                            items={reworkItems}
                            sortOrder={feedSort}
                            verificationCompose={verificationAttachKey ? verificationCompose : null}
                            verificationComposeEntryKey={verificationAttachKey}
                            excludeReworkIds={historyExcludeReworkIds}
                            requestEndDate={requestEndDate}
                        />
                    )}
                    {currentWorkReadOnly}
                </TaskActivityFeed>
            )}
        </DetailPanel>
    );
}

/**
 * 점검 상세 — 활동 피드 기반 진행 작업 관리 (신규 UI)
 */
function TaskManager({data, contentCategory, statusRows, verificationOnly = false}) {
    const config = EXECUTION_RECORD_CONFIG[contentCategory];

    const initialData = {
        inProgressCloseDate: data.inProgressCloseDate,
        inProgressDescription: data.inProgressDescription,
    };
    const [inProgressData, setInProgressData] = useState(initialData);
    const [reworkList, setReworkList] = useState([]);

    const {t} = useTranslation();
    const refreshContent = useSelector((state) => state.status.refreshContent);
    const statusNumericValue = useSelector((state) => state.content.statusNumericValue);
    const currentUser = useSelector((state) => state.auth.user);
    const dispatch = useDispatch();

    const showSection =
        data.inProgressUserId
        || statusNumericValue === ContentStatus.IN_PROGRESS.statusNumericValue
        || statusNumericValue === ContentStatus.VERIFICATION.statusNumericValue
        || statusNumericValue > ContentStatus.IN_PROGRESS.statusNumericValue;
    const canEdit = data.auth?.update ?? data.rudAuthDto?.update;
    const isActiveStep = statusNumericValue === ContentStatus.IN_PROGRESS.statusNumericValue;
    const showActiveEditor = showSection && isActiveStep && canEdit && !verificationOnly;

    const handleStatusUpdate = () => {
        dispatch(changeStatusNumericValue(0));
        dispatch(changeRefreshContentStatus());
        handleActiveToast({message: t("inProgressComplete")});
    };

    const fetchReworkList = useCallback(async () => {
        if (!config) return;
        try {
            const result = await getReworkList({id: data.id, type: config.reworkType});
            if (!result || result.status >= 400) {
                setReworkList([]);
                return;
            }
            setReworkList(normalizeReworkListResponse(result.data));
        } catch (error) {
            console.error(error);
            setReworkList([]);
        }
    }, [config, data.id]);

    const handleComplete = async () => {
        if (!inProgressData.inProgressDescription?.trim()) {
            window.alert("내용을 저장한 후 진행완료할 수 있습니다.");
            return;
        }
        if (!handleValidation(inProgressData.inProgressDescription, "내용을 저장해주세요.")) return;
        if (!window.confirm(t("changeNextStep"))) return;

        try {
            const workTime = inProgressData.inProgressCloseDate || convertDate();
            const contents = buildWorkProcess({
                type: config.reworkType,
                content: inProgressData.inProgressDescription,
                workTime,
                operatorId: currentUser?.id,
            });
            const response = await workDone(config.reworkType, data.id, contents);
            if (!response || response.status !== 200) {
                throw new Error("Work confirm failed");
            }
            await fetchReworkList();
            handleStatusUpdate();
        } catch (error) {
            handleActiveError({message: t("error"), description: error.message});
        }
    };

    useEffect(() => {
        fetchReworkList();
    }, [fetchReworkList, statusNumericValue, refreshContent]);

    if (!config) return null;

    const verificationConfig = getMaintenanceVerificationConfig(config.verificationVariant);
    const requestEndDate = data[resolveVerificationEndDateField(data, verificationConfig)];

    const hasHistory = showSection && Array.isArray(reworkList) && reworkList.length > 0;
    const panelTitle = config.progressPanelTitle ?? "요청 진행상태";
    const isVerificationStep = statusNumericValue === ContentStatus.VERIFICATION.statusNumericValue;
    const draftWork = showActiveEditor ? resolveCurrentDraftWork(reworkList) : null;
    const freshWorkInput = showActiveEditor && needsFreshWorkInput(reworkList);
    const pendingVerificationWork = isVerificationStep ? resolvePendingVerificationWork(reworkList) : null;
    const currentWorkBody = freshWorkInput
        ? ""
        : (
            (isVerificationStep && pendingVerificationWork?.content?.trim())
            || pendingVerificationWork?.content?.trim()
            || data.inProgressDescription?.trim()
            || draftWork?.content?.trim()
            || ""
        );

    const currentWorkReworkId = freshWorkInput
        ? null
        : (
            pendingVerificationWork?.reworkId
            ?? draftWork?.reworkId
            ?? resolveCurrentWorkReworkId(reworkList, currentWorkBody)
        );
    const workAlreadyInHistory = hasHistory && Boolean(
        pendingVerificationWork?.reworkId
        || (currentWorkBody && isLatestReworkWorkSame(reworkList, currentWorkBody)),
    );
    const historyExcludeReworkIds = [
        ...(showActiveEditor && draftWork?.reworkId ? [draftWork.reworkId] : []),
    ];
    const currentReworkEntry = findReworkEntry(reworkList, {
        reworkId: currentWorkReworkId,
        workBody: currentWorkBody,
    });
    const currentReworkFiles = currentReworkEntry?.files;
    const currentCycleWork = currentReworkEntry
        ? parseReworkResponseCycle(currentReworkEntry).work
        : null;
    const feedWorkContext = isVerificationStep && currentWorkBody && !workAlreadyInHistory
        ? {
            user: currentCycleWork?.user || resolveWorkUser(data),
            name: currentCycleWork?.name || resolveWorkAuthor(data) || undefined,
            time: currentCycleWork?.time || data.inProgressCloseDate,
            workBody: currentWorkBody,
            workDeadLine: currentCycleWork?.deadLine
                || resolveReworkDeadLine(currentReworkEntry)
                || requestEndDate,
            files: currentReworkFiles ?? currentCycleWork?.files,
            fileTable: (currentReworkFiles ?? currentCycleWork?.files)?.length
                ? undefined
                : reworkFileTable(currentWorkReworkId),
        }
        : null;
    const showCurrentWorkReadOnly = Boolean(
        showSection
        && currentWorkBody
        && !showActiveEditor
        && !isLatestReworkWorkSame(reworkList, currentWorkBody)
        && !feedWorkContext,
    );
    const verificationReviewContext = isVerificationStep && currentWorkReworkId
        ? {
            documentId: data.id,
            reworkId: currentWorkReworkId,
            contentCategory,
            initialOpinion: currentReworkEntry?.opinion,
            opinionSaved: Boolean(currentReworkEntry?.opinion?.trim()),
            onRefresh: fetchReworkList,
        }
        : null;
    const verificationAttachReworkId = isVerificationStep && workAlreadyInHistory && currentWorkReworkId
        ? currentWorkReworkId
        : null;

    if (!showSection && !statusRows) {
        return null;
    }

    return (
        <TaskProgressPanel
            title={panelTitle}
            statusRows={statusRows}
            reworkItems={hasHistory ? reworkList : []}
            verificationStepActive={
                isVerificationStep
                || statusNumericValue > ContentStatus.VERIFICATION.statusNumericValue
            }
            currentWorkReadOnly={
                showCurrentWorkReadOnly && !feedWorkContext ? (
                    <TaskActivityCycleCard
                        user={resolveWorkUser(data)}
                        name={resolveWorkAuthor(data) || undefined}
                        time={data.inProgressCloseDate}
                        workBody={currentWorkBody}
                        workDeadLine={currentCycleWork?.deadLine || resolveReworkDeadLine(currentReworkEntry) || requestEndDate}
                        files={currentReworkFiles}
                        fileTable={currentReworkFiles?.length
                            ? undefined
                            : reworkFileTable(currentWorkReworkId)}
                    />
                ) : null
            }
            verificationVariant={config.verificationVariant}
            verificationData={data}
            verificationFeedWorkContext={feedWorkContext}
            verificationWorkBody={isVerificationStep ? currentWorkBody : undefined}
            verificationAttachReworkId={verificationAttachReworkId}
            verificationReviewContext={verificationReviewContext}
            historyExcludeReworkIds={historyExcludeReworkIds}
            requestEndDate={requestEndDate}
            activeEditor={
                showActiveEditor ? (
                    <ActiveExecutionEditor
                        embedded
                        contentCategory={contentCategory}
                        currentWorkReworkId={currentWorkReworkId}
                        draftWork={freshWorkInput ? null : draftWork}
                        freshWorkInput={freshWorkInput}
                        contentLabel={hasHistory ? "내용" : "작업내역"}
                        data={data}
                        config={config}
                        requestEndDate={requestEndDate}
                        inProgressData={inProgressData}
                        setInProgressData={setInProgressData}
                        onComplete={handleComplete}
                        onReworkListRefresh={fetchReworkList}
                        refreshContent={refreshContent}
                    />
                ) : null
            }
        />
    );
}

export {TaskManager};
